import React, { useEffect, useState } from 'react';
import Button, { ButtonPrimary } from 'core/ui/Button';
import { Box, Dialog, DialogActions, DialogContent } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import Typography from 'core/ui/Typography';
import Texture from 'core/ui/Texture';
import useSelectors from 'modules/map/useSelectors';
import Modules from 'modules';
import useActions from 'modules/map/useActions';
import { useDispatch } from 'react-redux';
import notifications from 'modules/notifications';
import LoaderSm from 'core/ui/LoaderSm';
import SelectBoxObject from 'core/ui/SelectBoxObject';
import { Controller, useForm } from 'react-hook-form';
import { channelCurrency, gymDate } from 'core/utils/formatters';
import Paper from 'core/ui/Paper';
import Divider from 'core/ui/Divider';
import { ArrowForwardIos } from '@material-ui/icons';
import useConfirmation from 'core/useConfirmation';
import { channelKeysModule } from 'modules/channelKeys';

const ModalSwtichMembership = ({ showPlans, open, close, channel, channelKey, callbackSave }) => {
  const { t } = useTranslation();
  const loadingSwitch = useSelectors(channelKeysModule, 'loading');
  const { request } = useActions();
  const { confirmation } = useConfirmation();
  const [resume, setReume] = useState(null);
  const dispatch = useDispatch();

  const { control, watch, handleSubmit, reset } = useForm();

  useEffect(() => {
    if (!open) {
      reset({});
      setReume(null);
    }
  }, [open]);

  const switchResume = (channelMembershipPlanId) => {
    request({
      action: Modules.channelKeys.actions.checkSwitchresume,
      data: {
        channelId: channelKey?.channel?.id,
        channelKeyId: channelKey?.id,
        channelMembershipPlanId,
      },
      options: {
        onSuccess: (data) => {
          setReume(data);
        },
        onError: (ex) => {
          dispatch(notifications.error(ex));
        },
      },
    });
  };

  const confirmResume = (data) => {
    confirmation({
      description: t('mambership.switch.alert'),
      yesClick: () => {
        request({
          action: Modules.channelKeys.actions.confirmSwitchMembership,
          data: {
            channelId: channelKey?.channel?.id,
            channelKeyId: channelKey?.id,
            channelMembershipPlanId: data?.membershipPlanId,
          },
          options: {
            onSuccess: () => {
              if (callbackSave) {
                callbackSave();
              }
              dispatch(notifications.success(t('membership.switch.success')));

              close();
            },
            onError: (ex) => {
              dispatch(notifications.error(ex));
            },
          },
        });
      },
    });
  };

  useEffect(() => {
    if (watch('membershipPlanId')) {
      switchResume(watch('membershipPlanId'));
    }
  }, [watch('membershipPlanId')]);

  return (
    <Dialog open={open} onClose={close} maxWidth="sm" fullWidth>
      <Box>
        <Texture>
          <DialogContent>
            <Typography>{t('Switch Membership')}</Typography>

            <Box m={3}>
              <LoaderSm loading={loadingSwitch} center />
            </Box>

            <div className="tw-mb-2 tw-flex tw-flex-col tw-gap-4">
              <Controller
                control={control}
                render={({ field: { onChange, value } }) => (
                  <div>
                    <SelectBoxObject
                      label={t('label.membership.chooseNewPlan')}
                      options={
                        showPlans ||
                        (channel &&
                          channel.membershipPlans &&
                          channel.membershipPlans
                            .filter((x) => x.active === true)
                            .map((x) => ({ id: x.id, name: x.name })))
                      }
                      propLabel="name"
                      propValue="id"
                      setValue={onChange}
                      value={value}
                    />
                  </div>
                )}
                name="membershipPlanId"
              />
              {resume?.nextMembershipPlan && (
                <Paper p={2} className="tw-flex tw-flex-col tw-gap-2">
                  <div className="tw-flex tw-flex-row tw-items-center tw-justify-center tw-gap-2 tw-p-4">
                    <Typography>{resume?.currentMembershipPlan?.name}</Typography>
                    <ArrowForwardIos style={{ fontSize: 12 }} />
                    <Typography>{resume?.nextMembershipPlan?.name}</Typography>
                  </div>
                  <div>
                    <Typography>{t('label.membership.paymentType')}</Typography>
                    <Typography className="tw-text-lg">
                      {channelKey?.offline ? channelKey?.offlineType : 'ONLINE'}
                    </Typography>
                  </div>
                  <Divider />
                  <div>
                    <Typography>{t('label.membership.remainingAmount')}</Typography>
                    <Typography className="tw-text-lg">
                      {channelCurrency(resume?.currentCreditAmount, channel?.currency)}
                    </Typography>
                  </div>
                  <Divider />
                  <div>
                    <Typography>
                      {t(
                        resume?.due
                          ? 'label.membership.totalToPay'
                          : 'label.membership.totalDiffToPay',
                      )}
                    </Typography>
                    <Typography
                      className={resume?.due ? 'tw-text-lg' : 'tw-text-lg tw-text-green-500'}
                    >
                      {channelCurrency(resume?.totalToPay, channel?.currency)}
                    </Typography>
                    {resume?.due && (
                      <div>
                        <Typography className="tw-text-xs tw-text-blue-500">
                          {t('userPaymentMethod.defaultPayment')}
                        </Typography>
                      </div>
                    )}
                  </div>
                  {resume?.nextExpiryDate && (
                    <>
                      <Divider />
                      <div className="tw-flex tw-flex-row tw-gap-2">
                        <div className="flex-1">
                          <Typography>{t('label.membership.nextMembershipPrice')}</Typography>
                          <Typography className="tw-text-lg">
                            {channelCurrency(resume?.nextMembershipAmount, channel?.currency)}
                          </Typography>
                        </div>
                        <div className="tw-flex-1">
                          <Typography>
                            {t(
                              resume?.nextMembershipPlan?.renewable
                                ? 'label.membership.newPlanExpiryDate'
                                : 'label.membership.newPlanRenewalDate',
                            )}
                          </Typography>
                          <Typography className="tw-text-lg">
                            {gymDate(resume?.nextExpiryDate)}
                          </Typography>
                        </div>
                      </div>
                    </>
                  )}
                </Paper>
              )}
            </div>
          </DialogContent>
        </Texture>
        <DialogActions>
          <Button variant="outlined" onClick={close} color="secondary">
            {t('button.cancel')}
          </Button>
          <ButtonPrimary
            loading={loadingSwitch}
            disabled={loadingSwitch}
            onClick={() => handleSubmit(confirmResume)()}
            color="primary"
          >
            {t('button.confirm')}
          </ButtonPrimary>
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default ModalSwtichMembership;
